import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, CalendarDays, Loader2 } from 'lucide-react'
import Modal from '../ui/Modal'
import { api, ApiError } from '../../lib/api'
import { toast } from '../../lib/dialogs'
import { currentLocaleTag } from '../../lib/formatting'
import { todayAsInputDate } from '../../lib/datetime'
import type {
  PendingBillingItem,
  RescheduleRecurringPreview,
  RescheduleRecurringResponse,
} from '@jr2os/shared'

function errorMessage(err: unknown): string {
  const body = err instanceof ApiError ? (err.body as { error?: string } | undefined) : undefined
  return body?.error ?? (err instanceof Error ? err.message : 'Error')
}

/**
 * Suma días o meses a un día YYYY-MM-DD (en UTC, sin sorpresas de zona horaria). ☠️ Al sumar MESES el día se limita al último del
 * mes de destino: 31-oct + 1 mes es 30-nov, no 1-dic (con `Date.UTC` a pelo se desbordaba y se saltaba noviembre).
 */
function addToDay(day: string, add: { days?: number; months?: number }): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number]
  if (add.months) {
    const lastDayOfTarget = new Date(Date.UTC(y, m - 1 + add.months + 1, 0)).getUTCDate()
    return new Date(Date.UTC(y, m - 1 + add.months, Math.min(d, lastDayOfTarget)))
      .toISOString()
      .slice(0, 10)
  }
  return new Date(Date.UTC(y, m - 1, d + (add.days ?? 0))).toISOString().slice(0, 10)
}

/**
 * Reagendar una cuota o un hito de «Pendientes» (29-10-2026, Jesús: «aquí estaría bien poder reagendar las facturas pendientes»).
 *
 *  - HITO: cambia su fecha de vencimiento (cualquier fecha distinta); sigue pendiente de facturar.
 *  - CUOTA de un pago recurrente: mueve la fecha de la PRÓXIMA emisión de la iguala. ☠️ Solo se aplaza, y las cuotas programadas
 *    ANTES de la nueva fecha dejan de facturarse: la vista previa (la calcula el servidor con la misma lógica que al aplicar) dice
 *    cuántas y cuáles ANTES de confirmar. Es lo que se hizo a mano el 27-10 con las iguales ADaaS que esperaban a que el cliente
 *    pasara a producción.
 * El servidor recibe la fecha que se está viendo (`expected…`): si otra persona ya la facturó o la movió, rechaza (409) y no se pisa.
 */
export default function PendingRescheduleModal({
  item,
  onClose,
  onDone,
}: {
  item: PendingBillingItem | null
  onClose: () => void
  /** Se reagendó: quien lo abrió recarga la lista. */
  onDone: () => void
}) {
  const { t } = useTranslation('billing')
  const tag = currentLocaleTag()
  const [date, setDate] = useState('')
  const [reason, setReason] = useState('')
  const [preview, setPreview] = useState<RescheduleRecurringPreview | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const reqId = useRef(0)

  const current = item ? item.dueDate.slice(0, 10) : ''
  const isRecurring = item?.kind === 'RECURRING'
  const fmt = (day: string) =>
    new Date(`${day}T00:00:00Z`).toLocaleDateString(tag, { timeZone: 'UTC' })

  // Al abrir con otra fila, se empieza de cero.
  useEffect(() => {
    setDate('')
    setReason('')
    setPreview(null)
    setPreviewError(null)
    setError(null)
    setSaving(false)
    reqId.current += 1
  }, [item?.key])

  // Un pago recurrente solo se aplaza; un hito, a cualquier fecha distinta.
  const valid =
    /^\d{4}-\d{2}-\d{2}$/.test(date) && date !== current && (!isRecurring || date > current)

  // Vista previa de la iguala (con retraso, y descartando respuestas atrasadas).
  useEffect(() => {
    if (!item || !isRecurring || !valid) {
      setPreview(null)
      setPreviewError(null)
      setPreviewing(false)
      return
    }
    const my = ++reqId.current
    // Mientras llega la de la fecha nueva no se enseña la de la anterior (diría «facturará desde <fecha nueva>» con datos viejos).
    setPreview(null)
    setPreviewing(true)
    setPreviewError(null)
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const r = await api.post<RescheduleRecurringResponse>(
            `/recurring-invoices/${item.sourceId}/reschedule`,
            { nextRunDate: date, expectedRunDate: current, dryRun: true }
          )
          if (my !== reqId.current) return
          setPreview(r)
        } catch (err) {
          if (my !== reqId.current) return
          setPreview(null)
          setPreviewError(errorMessage(err))
        } finally {
          if (my === reqId.current) setPreviewing(false)
        }
      })()
    }, 300)
    return () => clearTimeout(timer)
  }, [item, isRecurring, valid, date, current])

  const confirm = async () => {
    if (!item || !valid || saving) return
    setSaving(true)
    setError(null)
    try {
      const trimmed = reason.trim()
      if (isRecurring) {
        await api.post(`/recurring-invoices/${item.sourceId}/reschedule`, {
          nextRunDate: date,
          expectedRunDate: current,
          ...(trimmed ? { reason: trimmed } : {}),
        })
        toast.success(t('pendingPanel.reschedule.doneRecurring', { date: fmt(date) }))
      } else {
        await api.post(`/milestones/${item.sourceId}/reschedule`, {
          dueDate: date,
          expectedDueDate: current,
          ...(trimmed ? { reason: trimmed } : {}),
        })
        toast.success(t('pendingPanel.reschedule.done', { date: fmt(date) }))
      }
      onDone()
    } catch (err) {
      // 409: otra persona ya cambió o facturó esto mientras la lista estaba abierta. Se avisa y se recarga la lista (si no, la fila
      // vieja seguiría ahí y volvería a dar el mismo 409 hasta recargar la página).
      if (err instanceof ApiError && err.status === 409) {
        toast.error(errorMessage(err))
        onDone()
        return
      }
      setError(errorMessage(err))
      setSaving(false)
    }
  }

  // Los atajos cuentan desde la mayor de «hoy» y la fecha actual: una cuota con meses de retraso no se «aplaza» a otra fecha pasada.
  const todayDay = todayAsInputDate()
  const base = current && current > todayDay ? current : todayDay
  const quick: Array<{ key: string; day: string }> = current
    ? [
        { key: 'week', day: addToDay(base, { days: 7 }) },
        { key: 'month', day: addToDay(base, { months: 1 }) },
        { key: 'quarter', day: addToDay(base, { months: 3 }) },
      ]
    : []

  const labels = preview?.skippedLabels.join(', ') ?? ''
  const moreLabels = preview ? preview.skippedCount - preview.skippedLabels.length : 0

  return (
    <Modal
      open={item !== null}
      onClose={() => (saving ? undefined : onClose())}
      title={t('pendingPanel.reschedule.title')}
      maxWidth="max-w-md"
    >
      {item && (
        <div className="space-y-4" data-testid="reschedule-modal">
          <div>
            <p className="text-sm font-semibold t-primary">
              {t('pendingPanel.reschedule.summary', {
                client: item.clientName,
                label: item.period ? `${item.label} · ${item.period}` : item.label,
              })}
            </p>
            <p className="text-xs t-muted mt-0.5">
              {t('pendingPanel.reschedule.currentDate', { date: fmt(current) })}
            </p>
            <p className="text-xs t-muted mt-1">
              {isRecurring
                ? t('pendingPanel.reschedule.recurringHint')
                : t('pendingPanel.reschedule.milestoneHint')}
            </p>
          </div>

          <div>
            <label className="block text-xs t-muted mb-1" htmlFor="reschedule-date">
              {t('pendingPanel.reschedule.newDate')}
            </label>
            <input
              id="reschedule-date"
              type="date"
              value={date}
              min={isRecurring ? addToDay(current, { days: 1 }) : undefined}
              max="2100-12-31"
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm surface-soft border border-base t-primary"
              data-testid="reschedule-date"
            />
            <div className="flex flex-wrap gap-1.5 mt-2">
              {quick.map((q) => (
                <button
                  key={q.key}
                  type="button"
                  onClick={() => setDate(q.day)}
                  className="px-2 py-1 rounded-lg text-[11px] border border-base t-secondary hover:bg-hover"
                >
                  {t(`pendingPanel.reschedule.quick.${q.key}`)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs t-muted mb-1" htmlFor="reschedule-reason">
              {t('pendingPanel.reschedule.reason')}
            </label>
            <input
              id="reschedule-reason"
              type="text"
              value={reason}
              maxLength={300}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('pendingPanel.reschedule.reasonPlaceholder')}
              className="w-full px-3 py-2 rounded-lg text-sm surface-soft border border-base t-primary"
            />
          </div>

          {isRecurring && valid && (
            <div
              className="text-xs rounded-lg p-2.5 border border-base surface-soft space-y-1.5"
              data-testid="reschedule-preview"
              aria-live="polite"
            >
              {previewing && !preview && (
                <span className="inline-flex items-center gap-1.5 t-muted">
                  <Loader2 size={12} className="animate-spin" /> {t('pendingPanel.reschedule.previewLoading')}
                </span>
              )}
              {previewError && <p className="text-rose-700 dark:text-rose-300">{previewError}</p>}
              {preview && (
                <>
                  <p className="t-secondary">
                    {preview.followingRunDate
                      ? t('pendingPanel.reschedule.previewFrom', {
                          date: fmt(date),
                          period: preview.newPeriodLabel,
                          following: fmt(preview.followingRunDate),
                        })
                      : t('pendingPanel.reschedule.previewFromLast', {
                          date: fmt(date),
                          period: preview.newPeriodLabel,
                        })}
                  </p>
                  {preview.skippedCount > 0 && preview.limitedByCount ? (
                    <p className="t-muted" data-testid="reschedule-limited">
                      {t('pendingPanel.reschedule.previewLimited', { labels })}
                      {moreLabels > 0 &&
                        t('pendingPanel.reschedule.previewSkippedMore', { count: moreLabels })}
                    </p>
                  ) : preview.skippedCount > 0 ? (
                    <p
                      className="flex items-start gap-1.5 text-amber-700 dark:text-amber-300"
                      data-testid="reschedule-skipped"
                    >
                      <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                      <span>
                        {t('pendingPanel.reschedule.previewSkipped', {
                          count: preview.skippedCount,
                          labels,
                        })}
                        {moreLabels > 0 &&
                          t('pendingPanel.reschedule.previewSkippedMore', { count: moreLabels })}
                      </span>
                    </p>
                  ) : (
                    <p className="t-muted">{t('pendingPanel.reschedule.previewNone')}</p>
                  )}
                </>
              )}
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 text-xs rounded-lg p-2.5 text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover disabled:opacity-50"
            >
              {t('common:actions.cancel')}
            </button>
            <button
              type="button"
              onClick={() => void confirm()}
              disabled={!valid || saving || (isRecurring && (previewing || !!previewError))}
              className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="reschedule-confirm"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <CalendarDays size={14} />}
              {t('pendingPanel.reschedule.confirm')}
            </button>
          </div>
        </div>
      )}
    </Modal>
  )
}
